import type { StrokePoint, StrokePoints } from "./stroke-shape.js";
import type { StrokeBounds } from "./stroke-corners.js";

/**
 * A character's strokes as a drill teaches them, in the square it writes in.
 *
 * A 汉字 writing drill (#962) draws the character's outline in the writing
 * square and marks each stroke the reader draws against the stroke the
 * outline wanted. Both sides of that comparison have to be in one frame, and
 * the frame is the square. This module puts the outline file's medians in it,
 * and `stroke-step.ts` does the marking.
 */

/**
 * The square every stroke of a drill is described in.
 *
 * The frame `characterShape` scales into. Both the medians and the ink are
 * handed over already scaled to it, so the frame is the identity and the
 * description keeps each stroke where it was.
 */
export const unitSquare: StrokeBounds = { left: 0, top: 0, span: 1 };

/**
 * The grid the outlines are drawn on, and where its baseline sits.
 *
 * Make Me a Hanzi's paths and medians use a 1024 by 1024 box whose y axis
 * decreases downwards, and `src/site/bishun-drawing.ts` rights them with
 * `scale(1, -1) translate(0, -900)`. The 900 is the font's own baseline
 * offset. A median point `(x, y)` therefore sits at `(x, 900 - y)` on the
 * square the figure is drawn in, and that is the square a drill writes in.
 */
const gridSize = 1024;
const gridBaseline = 900;

/**
 * The character's strokes, taken off the outline file and put in the square.
 *
 * @param medians - The `medians` field of a `hanzi-writer-data` file, one
 * polyline per stroke in writing order, on the 1024 grid.
 * @returns The same strokes in the unit square, the right way up.
 */
export function taughtStrokes(
  medians: readonly (readonly (readonly [number, number])[])[],
): readonly StrokePoints[] {
  return medians.map((median) =>
    median.map(([x, y]): StrokePoint => [
      x / gridSize,
      (gridBaseline - y) / gridSize,
    ]),
  );
}
